import { BuyerIntentType } from "./buyers";
import { TODO } from "./utilities";
import { UserEvent } from "./supabase-overrides";
import { Database } from "./supabase";

// Seller events are a subset of user events
export type SellerEvent = Extract<UserEvent, "claimed_home" | "booked_meeting" | "booked_online_meeting" | "requested_broker_valuation" | "performed_avm_estimate" | "requested_broker_valuation_after_avm_estimate" | "requested_broker_valuation_after_home_interaction" | "viewed_speculators_of_home" | "booked_broker_valuation" | "requested_meeting">;

// Seller intent from database enum
export type SellerIntentType = Database["public"]["Enums"]["seller_intent"];

export type Intent = {
  intent: SellerIntentType | BuyerIntentType;
  intentGivenAt: TODO;
  userId?: string;
  askingPrice?: number | null;
  pipelineStage: "NEW";
  pipelineFollowupAt?: TODO;
};

// Seller timeline: When they want to sell
export type SellerTimeline =
  | "sell_at_right_price"
  | "sell_now"
  | "sell_within_six_months"
  | "sell_within_twelve_months"
  | "sell_within_a_few_years";

export type SellerTimelineOption = {
  title: string;
  description: string;
  value: SellerTimeline;
};

export type SellerIntentOption = {
  title: string;
  description: string;
  value: SellerIntentType;
};

// Action intents that map to events
export const SELLER_ACTION_INTENTS: readonly SellerIntentType[] = [
  "claim",
  "meeting",
  "valuation",
  "estimate",
  "speculators",
] as const;

// Seller events: What actually gets executed in the backend (from database enum)
export const SELLER_EVENTS: readonly SellerEvent[] = [
  "claimed_home",
  "booked_meeting",
  "booked_online_meeting",
  "requested_broker_valuation",
  "performed_avm_estimate",
  "viewed_speculators_of_home",
  "booked_broker_valuation",
  "requested_meeting",
] as const;

// Helper to check if a UserAction is a seller event
export function isSellerEvent(action: string): action is SellerEvent {
  return SELLER_EVENTS.includes(action as SellerEvent);
}

// Map intents to their corresponding events
export const INTENT_TO_EVENT_MAP: Record<Extract<SellerIntentType, 'claim' | 'meeting' | 'valuation' | 'estimate' | 'speculators'>, SellerEvent> = {
  claim: "claimed_home",
  meeting: "requested_meeting",
  valuation: "requested_broker_valuation",
  estimate: "performed_avm_estimate",
  speculators: "viewed_speculators_of_home",
};

// Helper to check if an intent requires an event
export function isSellerEventIntent(intent: SellerIntentType): intent is 'claim' | 'meeting' | 'valuation' | 'estimate' | 'speculators' {
  return intent === 'claim' || intent === 'meeting' || intent === 'valuation' || intent === 'estimate' || intent === 'speculators';
}

// Helper to convert intent to event
export function intentToEvent(intent: SellerIntentType): SellerEvent | null {
  if (isSellerEventIntent(intent)) {
    return INTENT_TO_EVENT_MAP[intent];
  }
  return null;
}

export interface SellerActionData {
  addressData?: any; // Can be NestedAutocompleteAddress or Partial<Database["public"]["Tables"]["addresses"]["Insert"]>
  homeData?: any; // Can be NestedHome or Partial<Database["public"]["Tables"]["homes"]["Insert"]>
  brokerId?: string;
  leadSourceId?: string;
  assignmentSourceId?: string;
}
